import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { isNil, isNumber } from 'lodash-es';
import { UseQueryResult } from '@tanstack/react-query';
import { SortDescriptor } from '@ds';
import { LIST_ITEMS_COUNT } from '@/constants/lists.constants';
import { PaginatedBody, PaginationFilterType, UseQueryConfig } from '@/api/types';
import { useFilters, UseFiltersType } from './useFilters';
import { useFilterQueryParams } from './useFilterQueryParams';

type UseFilterQueryType<F extends Record<string, unknown>, R extends Record<string, unknown>, T, O> = {
  filterQuery: PaginationFilterType<R, O>;
  data?: PaginatedBody<T>;
  isLoading: boolean;
  isFetching: boolean;
  page: number;
  itemsPerPage: number;
  searchText: string;
  sortBy: SortDescriptor | undefined;
  setPage: (value: number) => void;
  setItemsPerPage: (value: number) => void;
  setSearchText: (value: string) => void;
  setSortBy: (value?: SortDescriptor) => void;
} & UseFiltersType<F>;

type UseFilterQueryProps<F extends Record<string, unknown>, R extends Record<string, unknown>, T, O> = {
  filterList: F;
  query: (params: PaginationFilterType<R, O>, options?: UseQueryConfig) => UseQueryResult<PaginatedBody<T>, Error>;
  defaultSortBy?: SortDescriptor;
  disabledKeys?: (keyof F)[];
  searchDebounceMs?: number;
};

const transformFilters = <F extends Record<string, unknown>>(filters: F): Partial<F> => {
  return Object.keys(filters).reduce((acc, key) => {
    const typedKey = key as keyof F;
    const value = filters[typedKey];

    if (Array.isArray(value)) {
      if (!value.length) return acc;

      return {
        ...acc,
        [typedKey]: value.map(String).join(','),
      };
    }

    if (typeof value === 'string') {
      if (!value.trim()) return acc;

      return {
        ...acc,
        [typedKey]: value,
      };
    }

    if (!isNil(value)) {
      return {
        ...acc,
        [typedKey]: value,
      };
    }

    return acc;
  }, {} as Partial<F>);
};

const getQueryParams = <F extends Record<string, unknown>, R extends Record<string, unknown>, O>(
  filters: F,
  sortBy: SortDescriptor | undefined,
  searchText: string,
  itemsPerPage: number,
  page: number,
): PaginationFilterType<R, O> => {
  const queryFilters = transformFilters(filters) as unknown as Partial<R>;

  return {
    ...queryFilters,
    searchQuery: searchText || undefined,
    orderBy: sortBy ? (sortBy.column as O) : undefined,
    asc: sortBy ? sortBy.direction === 'ascending' : undefined,
    pageIndex: page - 1,
    itemsPerPage,
  } as PaginationFilterType<R, O>;
};

export const useFilterQueryWithParams = <F extends Record<string, unknown>, R extends Record<string, unknown>, T, O>({
  filterList,
  query,
  defaultSortBy,
  disabledKeys,
  searchDebounceMs = 500,
}: UseFilterQueryProps<F, R, T, O>): UseFilterQueryType<F, R, T, O> => {
  const filterItemsPerPage = (filterList as Record<string, unknown>)?.itemsPerPage;
  const defaultItemsPerPage = isNumber(filterItemsPerPage) ? Number(filterItemsPerPage) : LIST_ITEMS_COUNT;

  const { initialState, syncToQueryParams } = useFilterQueryParams({
    defaultFilters: filterList,
    defaultItemsPerPage,
    defaultSortBy,
    disabledKeys,
  });

  const {
    filters,
    hasFilters,
    updateFilters: applyFilterUpdate,
    clearFilters: resetFilters,
    setFilters,
  } = useFilters(filterList, disabledKeys, initialState.filters);

  const [page, setPage] = useState(initialState.page);
  const [itemsPerPage, setItemsPerPageValue] = useState(initialState.itemsPerPage);
  const [searchText, setSearchTextValue] = useState(initialState.searchText);
  const [debouncedSearchText, setDebouncedSearchText] = useState(initialState.searchText);
  const [sortBy, setSortByValue] = useState<SortDescriptor | undefined>(initialState.sortBy);

  useDebounce(
    () => {
      setDebouncedSearchText(searchText);
    },
    searchDebounceMs,
    [searchText, searchDebounceMs],
  );

  const resetPage = useCallback(() => {
    setPage(1);
  }, []);

  const setItemsPerPage = useCallback(
    (value: number) => {
      setItemsPerPageValue(value);
      resetPage();
    },
    [resetPage],
  );

  const setSearchText = useCallback(
    (value: string) => {
      setSearchTextValue(value);
      resetPage();
    },
    [resetPage],
  );

  const setSortBy = useCallback(
    (value?: SortDescriptor) => {
      setSortByValue(value);
      resetPage();
    },
    [resetPage],
  );

  const updateFilters = useCallback(
    (filter: F[keyof F], type: keyof F) => {
      applyFilterUpdate(filter, type);
      resetPage();
    },
    [applyFilterUpdate, resetPage],
  );

  const clearFilters = useCallback(() => {
    resetFilters();
    resetPage();
    setItemsPerPageValue(defaultItemsPerPage);
    setSearchTextValue('');
    setDebouncedSearchText('');
    setSortByValue(defaultSortBy);
  }, [defaultItemsPerPage, defaultSortBy, resetFilters, resetPage]);

  const filterQuery = useMemo(
    () => getQueryParams<F, R, O>(filters, sortBy, debouncedSearchText, itemsPerPage, page),
    [filters, sortBy, debouncedSearchText, itemsPerPage, page],
  );

  useEffect(() => {
    syncToQueryParams({
      filters,
      page,
      itemsPerPage,
      searchText,
      sortBy,
    });
  }, [filters, itemsPerPage, page, searchText, sortBy, syncToQueryParams]);

  const { isLoading, isFetching, isRefetching, data } = query(filterQuery, {
    enabled: true,
  });

  return {
    filterQuery,
    data,
    isLoading,
    isFetching: isFetching || isRefetching,
    filters,
    hasFilters,
    updateFilters,
    clearFilters,
    setFilters,
    page,
    setPage,
    itemsPerPage,
    setItemsPerPage,
    sortBy,
    setSortBy,
    searchText,
    setSearchText,
  };
};
